import { create } from 'zustand';
import { ReportV2Listing } from '@/backend/access/reportV2/types';
import { ReportV2 } from '@/backend/models/reportV2';
import { InputRecord } from '@/backend/models/inputRecord';
import { ReportV2Version } from '@/backend/models/reportV2Version';
import { FileRef, InputValues, sheetWaveforms } from '@/reports/inputs';
import { ReportValues } from '@/reports/types';
import { I18n } from '@/i18n/I18n';
import { CreateReportV2Request, EnterpriseReportBundleAssignment, RecordResponse, ReportV2StateResponse, StartableReport, UpdateRecordRequest } from '@/types/reportV2Types';
import { FileController } from '../controllers/fileController';
import { ReportV2FrontendController } from '../controllers/reportV2FrontendController';
import { InputEdit, RecordDraft, RecordSheet, addEdit, draftBody, mergeDrafts, rebaseDraft, some } from './recordDrafts';

export type ReportV2State = Omit<ReportV2StateResponse, 'status' | 'errors' | 'toast'>;

/**
 * What was typed on a record and is not saved yet: what is being sent, what
 * was typed since (or failed to save, under what was typed since), and what is
 * held back because the record's newer sheet has no safe place for it — shown
 * as typed and never sent, until the page is reloaded.
 */
export interface RecordQueue {
  pending: RecordDraft | null;
  inFlight: RecordDraft | null;
  held: RecordDraft | null;
  /** the last save failed on the way (offline, a server error): it is tried again by itself, a while later, or with the next change */
  failed: boolean;
  /** the save in flight has not answered in `SLOW_MS` */
  slow: boolean;
  /** why the server refused the last save for good (the record is gone, a file is not on it…): not tried again until changed, or discarded */
  rejected: string | null;
}

/** How long a change waits for more typing before it saves. */
export const AUTOSAVE_MS = 700;
/** How long a save may take before it is shown as slow. */
export const SLOW_MS = 10_000;
/** How long a save may take before it is given up (aborted) and tried again — doubled after each failure in a row, up to 8× — the server ignores it if it still lands after a later one. */
export const SAVE_TIMEOUT_MS = 30_000;
/** How long a failed save waits before it is tried again by itself, after each failure in a row. */
export const RETRY_MS = [2_000, 5_000, 15_000, 30_000];

const emptyQueue: RecordQueue = { pending: null, inFlight: null, held: null, failed: false, slow: false, rejected: null };

/** Nothing on the record is left unsaved. */
export const allSaved = (q: RecordQueue | undefined) => !q || (!q.pending && !q.inFlight && !q.held);

/** A made-up id for this browser tab: with a rising seq per record, it orders the tab's saves on the server. */
const newSession = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`);

interface ReportV2Store {
  /** this org's reports; null until fetched */
  reports: ReportV2Listing[] | null;
  /** the bundles this org may start a report from; null until fetched */
  startable: StartableReport[] | null;
  states: Record<string, ReportV2State>;
  /** reports the viewer cannot see, or that do not exist */
  missing: Record<string, true>;
  versionsById: Record<string, ReportV2Version>;
  assignmentsByEnterprise: Record<string, EnterpriseReportBundleAssignment[]>;

  fetchReports: () => Promise<void>;
  fetchStartable: () => Promise<StartableReport[]>;
  create: (data: CreateReportV2Request) => Promise<ReportV2 | null>;
  fetchState: (id: string) => Promise<void>;
  /** save the title, the report's own values, or both; the report comes back as the server normalized it */
  save: (id: string, changes: { title?: string; values?: ReportValues }) => Promise<ReportV2 | null>;
  /**
   * save what was entered on the record a report renders; the record comes
   * back normalized to its sheet. Saved, or refused, on another schema version
   * than its reports here were drawn on — or naming another waveform — those
   * are fetched again first. The fill page saves through `edit` instead, in
   * order (`order`) and quietly
   */
  saveRecord: (recordId: string, values: InputValues, options?: Omit<UpdateRecordRequest, 'values'>) => Promise<InputRecord | null>;
  /** by record, what was typed on it and is not saved yet */
  queues: Record<string, RecordQueue>;
  /** a change typed on a report's fill page: shown at once on every report on its record, saved to the record a moment later */
  edit: (reportId: string, change: InputEdit) => void;
  /** save what is typed on a record now, one save at a time; true once nothing on it is left unsaved */
  flushRecord: (recordId: string) => Promise<boolean>;
  /** drop what is typed on a record and not saved (after a refusal for good) */
  discard: (recordId: string) => void;
  /** whether anything typed on any record is not saved */
  hasUnsaved: () => boolean;
  remove: (id: string) => Promise<boolean>;
  publish: (id: string, note?: string) => Promise<ReportV2Version | null>;
  fetchVersion: (id: string, versionId: string) => Promise<ReportV2Version | null>;
  /** upload a file against a record: the reference an input holds, or null when it failed */
  upload: (recordId: string, file: File, onProgress?: (percent: number) => void) => Promise<FileRef | null>;
  /** presigned links for files on a record */
  fileUrls: (recordId: string, fileIds: string[]) => Promise<Record<string, string>>;

  fetchAssignments: (enterpriseId: string) => Promise<void>;
  assign: (enterpriseId: string, reportBundleId: string) => Promise<void>;
  unassign: (enterpriseId: string, assignmentId: string) => Promise<void>;
  reset: () => void;
}

const initialState = {
  reports: null as ReportV2Listing[] | null,
  startable: null as StartableReport[] | null,
  states: {} as Record<string, ReportV2State>,
  missing: {} as Record<string, true>,
  versionsById: {} as Record<string, ReportV2Version>,
  assignmentsByEnterprise: {} as Record<string, EnterpriseReportBundleAssignment[]>,
  queues: {} as Record<string, RecordQueue>,
};

/** How a save went: saved, failed on the way (tried again), refused for a record that moved up (sent again at once), refused for good, or its record deleted meanwhile. */
type Outcome = 'saved' | 'failed' | 'again' | 'rejected' | 'gone';

// by record: the autosave (or retry) waiting to go, the save in flight, how many saves in a row failed, and the last seq sent
const timers = new Map<string, ReturnType<typeof setTimeout>>();
const flights = new Map<string, Promise<Outcome>>();
const failures = new Map<string, number>();
const seqs = new Map<string, number>();
let session = newSession();

/** The waveforms the inputs name, as a string that changes when one is added, swapped or taken away. */
const waveformKey = (state: ReportV2State, inputs: InputValues) => sheetWaveforms(state.sheet, inputs).map((w) => `${w.key}=${w.file.fileId}`).join('|');

/** Refused for good: anything 4xx but a lapsed sign-in, a timeout or too many requests. */
const refusedForGood = (status: number) => status >= 400 && status < 500 && ![401, 408, 429].includes(status);

export const useReportV2Store = create<ReportV2Store>((set, get) => {
  const queueOf = (recordId: string) => get().queues[recordId] ?? emptyQueue;
  const setQueue = (recordId: string, q: RecordQueue) => set({ queues: { ...get().queues, [recordId]: q } });

  /** The sheet a record is on, as its reports here were last fetched: the latest version among them. */
  const sheetOf = (recordId: string): RecordSheet | null => {
    const latest = Object.values(get().states).filter((st) => st.record.id === recordId).sort((a, b) => b.schema.versionNumber - a.schema.versionNumber)[0];
    return latest ? { sheet: latest.sheet, schemaVersionId: latest.schema.versionId } : null;
  };

  /** The queue with `later` over `earlier` as what is pending, both brought onto the record's sheet; what has no safe place there is held back. */
  const combine = (q: RecordQueue, recordId: string, earlier: RecordDraft | null, later: RecordDraft | null): RecordQueue => {
    const on = sheetOf(recordId);
    if (!on) return { ...q, pending: mergeDrafts(earlier, later) };
    const a = rebaseDraft(earlier, on);
    const b = rebaseDraft(later, on);
    return { ...q, pending: mergeDrafts(a.draft, b.draft), held: mergeDrafts(mergeDrafts(q.held, a.held), b.held) };
  };

  const schedule = (recordId: string, delay = AUTOSAVE_MS) => {
    clearTimeout(timers.get(recordId));
    timers.set(recordId, setTimeout(() => {
      timers.delete(recordId);
      void get().flushRecord(recordId);
    }, delay));
  };

  const unschedule = (recordId: string) => {
    clearTimeout(timers.get(recordId));
    timers.delete(recordId);
  };

  /**
   * A save's answer, shown on every report on the record: what was saved,
   * and the reports fetched again that were drawn on another schema version
   * than it is on now (it moved up) or that now name another waveform.
   */
  const applySaved = async (recordId: string, response: RecordResponse) => {
    const moved = (record: InputRecord) => Object.keys(get().states).filter((reportId) => {
      const state = get().states[reportId];
      return state.record.id === record.id && state.schema.versionId !== record.schemaVersionId;
    });
    const refetch = (reportIds: string[]) => Promise.all(reportIds.map((reportId) => get().fetchState(reportId)));
    if (response.status !== 200 || !response.record) {
      // a save refused as drawn from an earlier version carries the record as it is
      if (response.record?.id === recordId) await refetch(moved(response.record));
      return;
    }
    const record = response.record;
    const stale = [...new Set([...moved(record), ...Object.keys(get().states).filter((reportId) => {
      const state = get().states[reportId];
      return state.record.id === record.id && waveformKey(state, state.record.values) !== waveformKey(state, record.values);
    })])];
    // every report on the record renders what was just saved, not only the one it was typed into
    set({ states: Object.fromEntries(Object.entries(get().states).map(([reportId, state]) => [reportId, state.record.id === record.id ? { ...state, record } : state])) });
    await refetch(stale);
  };

  /**
   * Send what is pending on the record, quietly (the page says how it went),
   * with this tab's next seq for it. A save that has not answered in
   * `SAVE_TIMEOUT_MS` is aborted and counts as failed: if it still lands, the
   * server ignores it once a later one from this tab has. A failure goes back
   * under what was typed since, never on its own.
   */
  const sendPending = async (recordId: string, sending: RecordDraft): Promise<Outcome> => {
    const abort = new AbortController();
    // longer after each failure in a row, so a save that always takes a while still lands
    const timeout = setTimeout(() => abort.abort(), SAVE_TIMEOUT_MS * 2 ** Math.min(failures.get(recordId) ?? 0, 3));
    const slow = setTimeout(() => { if (recordId in get().queues) setQueue(recordId, { ...queueOf(recordId), slow: true }); }, SLOW_MS);
    const seq = (seqs.get(recordId) ?? 0) + 1;
    seqs.set(recordId, seq);
    let response: RecordResponse | null = null;
    try {
      const { values, cells } = draftBody(sending);
      response = await new ReportV2FrontendController().updateRecord(recordId, { values, cells, schemaVersionId: sending.schemaVersionId, order: { session, seq } }, { quiet: true, signal: abort.signal });
      await applySaved(recordId, response);
    } catch {
      // not even sent (signed out, say): failed like any other
    } finally {
      clearTimeout(timeout);
      clearTimeout(slow);
    }
    // the record's last report was deleted meanwhile, and what was typed on it with it
    if (!(recordId in get().queues)) return 'gone';
    const now = queueOf(recordId);
    const status = response?.status ?? 0;
    if (status === 200 && response?.record) {
      failures.delete(recordId);
      setQueue(recordId, { ...now, inFlight: null, failed: false, slow: false, rejected: null });
      return 'saved';
    }
    const back = { ...combine(now, recordId, sending, now.pending), inFlight: null, slow: false };
    // refused for a record that has moved up since: its reports were fetched again, so it goes again on the record's sheet
    const on = sheetOf(recordId);
    if (status === 422 && response?.record && on && on.schemaVersionId !== sending.schemaVersionId) {
      setQueue(recordId, { ...back, failed: true });
      return 'again';
    }
    if (refusedForGood(status)) {
      setQueue(recordId, { ...back, failed: false, rejected: response?.errors?.[0]?.message ?? I18n.get('unknownError') });
      return 'rejected';
    }
    setQueue(recordId, { ...back, failed: true });
    return 'failed';
  };

  /** Once a save is done: a failure is tried again by itself, later each time; what was typed meanwhile goes now. */
  const afterFlight = (recordId: string, outcome: Outcome) => {
    if (outcome === 'gone' || outcome === 'rejected') return;
    if (outcome === 'failed') {
      const n = failures.get(recordId) ?? 0;
      failures.set(recordId, n + 1);
      schedule(recordId, RETRY_MS[Math.min(n, RETRY_MS.length - 1)]);
    } else if (queueOf(recordId).pending && !timers.has(recordId)) {
      schedule(recordId);
    }
  };

  return {
    ...initialState,

    edit: (reportId, change) => {
      const state = get().states[reportId];
      if (!state) return;
      const recordId = state.record.id;
      const q = queueOf(recordId);
      // typed again, an input held back is still the older sheet's copy: it stays held
      if (q.held && change.inputId in q.held.whole) {
        setQueue(recordId, { ...q, held: addEdit(q.held, q.held, change) });
        return;
      }
      const typed = addEdit(null, { sheet: state.sheet, schemaVersionId: state.schema.versionId }, change);
      setQueue(recordId, combine(q, recordId, q.pending, typed));
      schedule(recordId);
    },

    flushRecord: async (recordId) => {
      unschedule(recordId);
      // one save at a time on a record: what was typed meanwhile goes once the one in flight is done
      const flying = flights.get(recordId);
      if (flying) {
        await flying;
        return get().flushRecord(recordId);
      }
      if (!(recordId in get().queues)) return true;
      const q = combine(queueOf(recordId), recordId, queueOf(recordId).pending, null);
      const sending = some(q.pending);
      if (!sending) {
        setQueue(recordId, { ...q, pending: null, failed: false });
        return allSaved(get().queues[recordId]);
      }
      setQueue(recordId, { ...q, pending: null, inFlight: sending });
      const flight = sendPending(recordId, sending).then((outcome) => {
        flights.delete(recordId);
        afterFlight(recordId, outcome);
        return outcome;
      });
      flights.set(recordId, flight);
      const outcome = await flight;
      if (outcome !== 'saved' && outcome !== 'again') return false;
      // what was typed meanwhile, or what failed for a record that moved up, goes now
      return queueOf(recordId).pending ? get().flushRecord(recordId) : allSaved(get().queues[recordId]);
    },

    discard: (recordId) => {
      const q = get().queues[recordId];
      if (!q) return;
      unschedule(recordId);
      failures.delete(recordId);
      setQueue(recordId, { ...q, pending: null, failed: false, rejected: null });
    },

    hasUnsaved: () => Object.values(get().queues).some((q) => !!(q.pending || q.inFlight || q.held)),

    fetchReports: async () => {
      const response = await new ReportV2FrontendController().list();
      set({ reports: response.status === 200 ? response.reports : [] });
    },

    fetchStartable: async () => {
      const response = await new ReportV2FrontendController().startable();
      const startable = response.status === 200 ? response.reports : [];
      set({ startable });
      return startable;
    },

    create: async (data) => {
      const response = await new ReportV2FrontendController().create(data);
      if (response.status !== 201 || !response.report) return null;
      set({ reports: null });
      return response.report;
    },

    fetchState: async (id) => {
      const response = await new ReportV2FrontendController().read(id);
      if (response.status !== 200) {
        if (response.status === 404) set({ missing: { ...get().missing, [id]: true } });
        return;
      }
      const { status: _status, errors: _errors, toast: _toast, ...state } = response;
      // a save that landed while this was read leaves the cached record the newer one: it stays, while on the sheet read with it
      const cached = get().states[id]?.record;
      const newer = cached?.id === state.record.id && cached.schemaVersionId === state.record.schemaVersionId && Date.parse(String(cached.updatedAt ?? '')) > Date.parse(String(state.record.updatedAt ?? ''));
      set({ states: { ...get().states, [id]: newer ? { ...state, record: cached } : state } });
    },

    save: async (id, changes) => {
      const response = await new ReportV2FrontendController().update(id, changes);
      if (response.status !== 200 || !response.report) return null;
      const current = get().states[id];
      if (current) set({ states: { ...get().states, [id]: { ...current, report: response.report } } });
      return response.report;
    },

    saveRecord: async (recordId, values, options) => {
      const response = await new ReportV2FrontendController().updateRecord(recordId, { ...options, values });
      await applySaved(recordId, response);
      return response.status === 200 && response.record ? response.record : null;
    },

    remove: async (id) => {
      // what is typed on the record is saved first: the record may well outlive the report
      const recordId = get().states[id]?.record.id ?? null;
      if (recordId) {
        await get().flushRecord(recordId);
        unschedule(recordId);
      }
      const response = await new ReportV2FrontendController().delete(id);
      // only the server knows whether this was the last report on the record: the record, and what was typed on it, go only when it says so
      const recordGone = response.status === 200 && !!response.recordDeleted && !!recordId;
      if (!recordGone && recordId && queueOf(recordId).pending) schedule(recordId);
      if (response.status !== 200) return false;
      const { [id]: _gone, ...states } = get().states;
      const queues = { ...get().queues };
      if (recordGone && recordId) {
        failures.delete(recordId);
        delete queues[recordId];
      }
      set({ states, reports: null, queues });
      return true;
    },

    publish: async (id, note) => {
      // what is typed on the record is saved first: nothing is published over what is not
      const recordId = get().states[id]?.record.id;
      if (recordId && !(await get().flushRecord(recordId))) return null;
      const response = await new ReportV2FrontendController().publish(id, { note });
      if (response.status !== 201 || !response.version) return null;
      const version = response.version;
      const current = get().states[id];
      const { inputs: _inputs, values: _values, context: _context, ...summary } = version;
      set({
        versionsById: { ...get().versionsById, [version.id]: version },
        ...(current ? { states: { ...get().states, [id]: { ...current, versions: [summary, ...current.versions] } } } : {}),
        reports: null,
      });
      return version;
    },

    fetchVersion: async (id, versionId) => {
      const cached = get().versionsById[versionId];
      if (cached) return cached;
      const response = await new ReportV2FrontendController().readVersion(id, versionId);
      if (response.status !== 200 || !response.version) return null;
      set({ versionsById: { ...get().versionsById, [versionId]: response.version } });
      return response.version;
    },

    upload: async (recordId, file, onProgress) => {
      const created = await new ReportV2FrontendController().upload(recordId, file.name);
      if (created.status !== 201 || !created.uploadUrl) return null;
      const put = await new FileController().uploadToS3(created.uploadUrl, file, onProgress ?? (() => undefined));
      return put.ok ? created.file : null;
    },

    fileUrls: async (recordId, fileIds) => {
      if (fileIds.length === 0) return {};
      const response = await new ReportV2FrontendController().fileUrls(recordId, fileIds);
      return response.status === 200 ? response.urls : {};
    },

    fetchAssignments: async (enterpriseId) => {
      const response = await new ReportV2FrontendController().assignmentsForEnterprise(enterpriseId);
      if (response.status === 200) set({ assignmentsByEnterprise: { ...get().assignmentsByEnterprise, [enterpriseId]: response.assignments } });
    },

    assign: async (enterpriseId, reportBundleId) => {
      const response = await new ReportV2FrontendController().assign(enterpriseId, reportBundleId);
      if (response.status === 201) await get().fetchAssignments(enterpriseId);
    },

    unassign: async (enterpriseId, assignmentId) => {
      const response = await new ReportV2FrontendController().unassign(assignmentId);
      if (response.status === 200) await get().fetchAssignments(enterpriseId);
    },

    reset: () => {
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
      flights.clear();
      failures.clear();
      // a new session, so the server never takes this tab's next saves for old ones
      seqs.clear();
      session = newSession();
      set({ ...initialState });
    },
  };
});

// leaving the app, or reloading, while anything typed on a record is not saved asks first — from any page, not only the one it was typed on
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (e) => {
    if (useReportV2Store.getState().hasUnsaved()) e.preventDefault();
  });
}
